import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { find as _find } from 'lodash-es';
import { useLocation } from 'react-router-dom';
import { Grid, IconButton } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { observer } from 'mobx-react';

import { capitalize } from 'core/helpers/formatters';
import copyIcon from 'core/assets/svg/copy.svg';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import ScreenHeader from 'core/components/screen-header';
import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import useSession from 'core/hooks/use-session';
import { EditReferral } from './components/edit-referral';
import LinkedCustomer from './components/linked-customer';
import PotentialMatches from './components/potential-matches';
import PotentialCustomerMatches from './components/potential-customer-matches';
import { SupportingDocumentsButton } from '../offer-document-upload';
import useStyles from './header.styles';

const getCoappName = (drivers) => {
  if (drivers && drivers.length) {
    const coApp = _find(drivers, { isCoApplicant: true });
    if (coApp) {
      return `${capitalize(coApp.firstName.trim())} ${capitalize(coApp.lastName.trim())}`;
    }
  }
  return undefined;
};

const getFullAddressString = (correctedAddress) => {
  return `${correctedAddress.address} ${correctedAddress.address2 || ''} ${correctedAddress.city} ${
    correctedAddress.state
  } ${correctedAddress.zip}`;
};

const OfferHeader = ({ offerId, history, options, onBack }) => {
  const classes = useStyles();
  const location = useLocation();
  const toast = useToast();
  const { isAgency, isExternalSales } = useSession();
  const fromCheckout = location.pathname.includes('checkout');

  const {
    offer: { loading, offer },
    matchSearch
  } = useStore();

  useEffect(() => {
    // Set initial search string only when modal opens and search is empty
    if (offer && !loading && !matchSearch.loading && !matchSearch.searchString) {
      let expectedSearchString;

      if (offer.quote?.existingCustomer?.id) {
        expectedSearchString = offer.quote.existingCustomer.id;
      } else if (offer.quote?.fname && offer.quote?.lname) {
        const offerState = offer?.state || offer?.quote?.correctedAddress?.state || offer?.quote?.address?.state || '';
        const state = offerState ? ` ${offerState}` : '';
        expectedSearchString = `${offer.quote.fname} ${offer.quote.lname}${state}`;
      }

      if (expectedSearchString) {
        matchSearch.setCurrentResultsPage(0);
        matchSearch.setSearchString(expectedSearchString);
        matchSearch.searchCustomersAction();
        matchSearch.searchOffersAction();
      }
    }
  }, [
    matchSearch.searchCustomersAction,
    matchSearch.searchString,
    loading,
    matchSearch.loading,
    offer?.quote?.existingCustomer?.id
  ]);

  useEffect(() => {
    // Reset search string when offer changes
    matchSearch.setSearchString('');
  }, [offer?.offer?.id]);

  let titleString;
  if ((loading && !offer) || !offer?.quote?.fname || !offer?.quote?.lname) {
    titleString = 'Loading...';
  } else if (options.showAddress) {
    titleString = `${offer.quote.fname} ${offer.quote.lname} - ${getFullAddressString(offer.quote.correctedAddress)}`;
  } else {
    titleString = `${offer.quote.fname} ${offer.quote.lname}`;
  }

  let matchCustomer;
  if (matchSearch.loading || (loading && !offer) || !matchSearch.searchString) {
    matchCustomer = <Loading noBackground />;
  } else if (!offer?.quote?.existingCustomer?.id) {
    matchCustomer = (
      <PotentialMatches
        classes={classes}
        hits={matchSearch?.customersResults?.nbHits}
        fromCheckout={fromCheckout}
        matchLabel={
          matchSearch?.customersResults?.nbHits ? (
            <>
              We&#39;ve found <b>{matchSearch?.customersResults?.nbHits || 0}</b> &nbsp;{titleString}
            </>
          ) : (
            <>No matches to current members</>
          )
        }
        setMatchesResult={matchSearch.setShowCustomerMatches}
        history={history}
        offerId={offerId}
      />
    );
  } else if (matchSearch.customersResults?.hits) {
    matchCustomer = (
      <LinkedCustomer
        classes={classes}
        link={`/customer/${offer?.quote?.existingCustomer?.id}`}
        label={`Linked to ${matchSearch?.customersResults?.hits[0]?.attributes?.name} ${matchSearch.customersResults?.hits[0]?.attributes?.family_name} ID: ${offer?.quote?.existingCustomer?.id}`}
        containerClassName={classes.matchCustomer}
        fromCheckout={fromCheckout}
        setMatchesResult={matchSearch.setShowCustomerMatches}
        history={history}
        offerId={offerId}
      />
    );
  }

  const copyPreBindId = () => {
    const copied = copy(offer.webUserId);
    if (copied) {
      toast.notify({
        type: 'success',
        message: 'Pre-Bind ID was copied to your clipboard.'
      });
    }
  };

  const coappName = getCoappName(offer?.quote?.drivers);
  const subtitles = !loading ? (
    <Grid container>
      {offer?.quote?.drivers && coappName && (
        <Grid container item direction="row" alignItems="baseline">
          <Label className={classes.subtitle} type="infoLabel">
            Co-Applicant
          </Label>
          <Label type="infoValue">{coappName}</Label>
        </Grid>
      )}
      {offer?.webUserId && (
        <Grid container item direction="row" alignItems="baseline">
          <Label className={classes.subtitle} type="infoLabel">
            Pre-bind ID
          </Label>
          <Label type="infoValue">
            <Grid container alignItems="center">
              {offer.webUserId}
              <IconButton onClick={copyPreBindId} className={classes.copyButton}>
                <img src={copyIcon} alt="copy" />
              </IconButton>
            </Grid>
          </Label>
        </Grid>
      )}
      {!offer?.quote?.existingCustomer && !(isAgency || isExternalSales) && (
        <EditReferral fromCheckout={fromCheckout} inviter={offer?.quote?.inviter} />
      )}
      {offer?.quote?.rep && (
        <Grid container item direction="row" alignItems="baseline">
          <Label className={classes.subtitle} type="infoLabel">
            Rep
          </Label>
          <Label type="infoValue">{offer.quote.rep}</Label>
        </Grid>
      )}
    </Grid>
  ) : undefined;

  if (!options.showCustomerMatches && !options.showOfferMatches && !isAgency) {
    return (
      <div id="offer_header">
        <ScreenHeader
          title={titleString}
          subtitles={subtitles}
          onBack={() => history.push('/search/offers')}
          options={options}
          rightValue={<SupportingDocumentsButton />}
        />
      </div>
    );
  }

  const rightValue = !isAgency ? (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <SupportingDocumentsButton />
      </Grid>
      <Grid item>
        {PotentialCustomerMatches({
          matchCustomer
        })}
      </Grid>
    </Grid>
  ) : (
    <Grid>
      {PotentialCustomerMatches({
        matchCustomer
      })}
    </Grid>
  );

  return (
    <div id="offer_header">
      <ScreenHeader
        title={titleString}
        subtitles={subtitles}
        onBack={onBack}
        options={options}
        rightValue={rightValue}
      />
    </div>
  );
};

OfferHeader.defaultProps = {
  options: { showAddress: false, hideBackButton: false, showCustomerMatches: true, showOfferMatches: true },
  history: {},
  onBack: () => {}
};

OfferHeader.propTypes = {
  offerId: PropTypes.string.isRequired,
  history: PropTypes.object,
  options: PropTypes.object,
  onBack: PropTypes.func
};

export default observer(OfferHeader);
